import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  Container
} from '@mui/material';
import {
  TrendingUp,
  Schedule,
  CheckCircle,
  Error,
  ArrowBack,
  LocalShipping,
} from '@mui/icons-material';
import { suppliersAPI } from '../../services/API';

export const SupplierAnalytics = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [id]);

  const fetchAnalytics = async () => {
    try {
      const response = await suppliersAPI.getAnalytics(id);
      setAnalytics(response.data.data.analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!analytics) {
    return (
      <Alert severity="error">
        Analytics data not available
      </Alert>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
      {/* Back button and header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/hospital/pharmacy/suppliers')}
          variant="outlined"
        >
          Back to Suppliers
        </Button>
        <Typography variant="h3" fontWeight="bold">
          Supplier Analytics
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Key Metrics */}
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TrendingUp sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h3" fontWeight="bold" color="primary.main">
                {analytics.fulfillmentRate}%
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Order Fulfillment Rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Schedule sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
              <Typography variant="h3" fontWeight="bold" color="info.main">
                {analytics.onTimeDelivery}%
              </Typography>
              <Typography variant="h6" color="text.secondary">
                On-Time Delivery
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h3" fontWeight="bold" color="success.main">
                {analytics.qualityScore}%
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Quality Score
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <LocalShipping sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h3" fontWeight="bold" color="warning.main">
                {analytics.avgLeadTime}
              </Typography>
              <Typography variant="h6" color="text.secondary">
                Avg Lead Time (Days)
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Performance Trends */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Monthly Performance
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Orders</TableCell>
                      <TableCell align="right">Fulfillment Rate</TableCell>
                      <TableCell align="right">On-Time Delivery</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analytics.monthlyPerformance?.map((month) => (
                      <TableRow key={month.month}>
                        <TableCell>
                          <Typography variant="h6" fontWeight="medium">
                            {month.month}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="h6">
                            {month.orders}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${month.fulfillmentRate}%`}
                            size="small"
                            color={month.fulfillmentRate >= 95 ? 'success' : month.fulfillmentRate >= 85 ? 'warning' : 'error'}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${month.onTimeDelivery}%`}
                            size="small"
                            color={month.onTimeDelivery >= 90 ? 'success' : month.onTimeDelivery >= 80 ? 'warning' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Quality Metrics */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight="bold">
                Quality Metrics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Defect Rate</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {analytics.defectRate}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={100 - analytics.defectRate}
                    color={analytics.defectRate < 2 ? 'success' : analytics.defectRate < 5 ? 'warning' : 'error'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Return Rate</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {analytics.returnRate}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={100 - analytics.returnRate}
                    color={analytics.returnRate < 1 ? 'success' : analytics.returnRate < 3 ? 'warning' : 'error'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">Compliance Score</Typography>
                    <Typography variant="h6" fontWeight="bold">
                      {analytics.complianceScore}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={analytics.complianceScore}
                    color={analytics.complianceScore >= 95 ? 'success' : analytics.complianceScore >= 85 ? 'warning' : 'error'}
                    sx={{ height: 8, borderRadius: 4 }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Issues */}
        {analytics.recentIssues && analytics.recentIssues.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" gutterBottom fontWeight="bold">
                  Recent Issues
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Issue Type</TableCell>
                        <TableCell>Order Number</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Severity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.recentIssues.map((issue) => (
                        <TableRow key={issue.id}>
                          <TableCell>
                            <Typography variant="h6">
                              {new Date(issue.date).toLocaleDateString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="h6" fontWeight="medium">
                              {issue.type}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="h6">
                              {issue.orderNumber}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={issue.status}
                              size="small"
                              color={issue.status === 'Resolved' ? 'success' : issue.status === 'In Progress' ? 'warning' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={issue.severity}
                              size="small"
                              color={issue.severity === 'High' ? 'error' : issue.severity === 'Medium' ? 'warning' : 'info'}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
      </Container>
    </Box>
  );
};